import { useMemo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import {
  DAY_GRID_HOURS,
  DAY_GRID_HOUR_START,
  DAY_GRID_HOUR_END,
  dayTimelineBlocks,
  fmtHourLabel,
  fmtTime,
  formatMinutes,
  prettifySlug,
  paletteFor,
  isSameDay,
  startOfDay,
  DAY_FULL,
  MONTH_SHORT,
} from "../lib/calendarUtils";

// CalendarDay — per-minute timeline view of a single day.
//
// Architecture:
//   - The hour grid renders as a backdrop: hour labels on the left
//     rail, faint horizontal lines marking each hour. Tapping an
//     empty hour cell opens the recipe picker for that hour.
//   - Cooking events render as ABSOLUTELY-POSITIONED blocks on top.
//     A block's top + height correspond to the cook's actual start
//     time and duration in minutes — a 6-hour brioche occupies 6
//     hours of vertical space, not a 1-hour slot.
//   - Overlapping blocks split into LANES. When two cooks happen
//     at the same time (hash brown sides at 6pm next to a steak),
//     they sit side-by-side at half-width.
//   - Cooked + planned MERGE: a scheduled meal that's been cooked
//     renders as ONE block (kind="cooked") with the cook_log's
//     actual cooked_at time. Standalone cook_logs (no plan) render
//     on their own.
//   - Today's view: past minutes have a translucent grey overlay,
//     and a 2px horizontal "now" line marks the current minute.
//     Repaints every 60s.

const HOUR_ROW_HEIGHT = 56;
const HOUR_LABEL_WIDTH = 60;
const MINUTES_PER_HOUR = 60;
const GRID_TOTAL_HOURS = DAY_GRID_HOUR_END - DAY_GRID_HOUR_START + 1;
const GRID_TOTAL_HEIGHT = GRID_TOTAL_HOURS * HOUR_ROW_HEIGHT;
// When a conflict group has 3+ overlapping tiles, switch from
// percentage-split lanes (which squish each tile to <33%) to fixed-
// width tiles + horizontal scroll. ~2.25 tiles visible at a time
// in a typical mobile viewport; the user swipes right to find the
// 3rd, 4th, etc. — readable at a glance instead of crammed.
const HSCROLL_THRESHOLD = 3;
const HSCROLL_LANE_WIDTH = 220;
const HSCROLL_LANE_GAP = 6;

function minuteToY(minutes) {
  // Clamp to grid bounds. Out-of-range events render at the top
  // (early-morning) or bottom (late-night) edge.
  const startMinAbs = DAY_GRID_HOUR_START * MINUTES_PER_HOUR;
  const endMinAbs   = (DAY_GRID_HOUR_END + 1) * MINUTES_PER_HOUR;
  if (minutes < startMinAbs) return 0;
  if (minutes >= endMinAbs)  return GRID_TOTAL_HEIGHT;
  return ((minutes - startMinAbs) / MINUTES_PER_HOUR) * HOUR_ROW_HEIGHT;
}

function durationToHeight(durationMin) {
  const minHeight = 36;
  const px = (durationMin / MINUTES_PER_HOUR) * HOUR_ROW_HEIGHT;
  return Math.max(minHeight, px);
}

// Per-conflict-group lane assignment. Two blocks share a "conflict
// group" only when they (transitively) overlap in time — a 3 PM
// 10-min cook in lane 0 doesn't share a group with a 5 PM 8-hour
// brioche, so each fills its full row instead of the brioche
// stealing the slim 33% slot from the early-afternoon tile.
//
// Algorithm:
//   1. Sort by start.
//   2. Walk forward. When the next block starts AFTER the current
//      group's max-end, the group closes (no future overlap is
//      possible with closed-group blocks). Open a fresh group.
//   3. Inside a group, do the standard "first lane whose last
//      block ended" greedy assignment.
//   4. Stamp each block with `lane` (column inside its group) +
//      `groupTotalLanes` (total columns in its group).
//
// The renderer reads `block.groupTotalLanes` instead of a global
// total so width/left calc becomes per-group, not per-day.
function assignLanes(blocks) {
  const sorted = [...blocks].sort((a, b) => a.startMinutes - b.startMinutes);
  let groupBlocks = [];
  let groupLaneEnds = [];
  let groupEnd = -Infinity;
  let maxLanesAcrossDay = 0;
  let nextGroupId = 0;
  let currentGroupId = 0;

  const closeGroup = () => {
    const total = Math.max(1, groupLaneEnds.length);
    for (const b of groupBlocks) b.groupTotalLanes = total;
    if (total > maxLanesAcrossDay) maxLanesAcrossDay = total;
    groupBlocks = [];
    groupLaneEnds = [];
    groupEnd = -Infinity;
  };

  for (const b of sorted) {
    if (b.startMinutes >= groupEnd) {
      closeGroup();
      currentGroupId = nextGroupId++;
    }
    const blockEnd = b.startMinutes + b.durationMinutes;
    let assigned = -1;
    for (let i = 0; i < groupLaneEnds.length; i++) {
      if (groupLaneEnds[i] <= b.startMinutes) { assigned = i; break; }
    }
    if (assigned === -1) {
      assigned = groupLaneEnds.length;
      groupLaneEnds.push(blockEnd);
    } else {
      groupLaneEnds[assigned] = blockEnd;
    }
    b.lane = assigned;
    b.groupId = currentGroupId;
    groupBlocks.push(b);
    if (blockEnd > groupEnd) groupEnd = blockEnd;
  }
  closeGroup();
  return Math.max(1, maxLanesAcrossDay);
}

export default function CalendarDay({
  cursorDate,
  meals = [],
  pastCooks = [],
  prepRows = [],
  pantry = [],
  userId,
  nameFor,
  findRecipe,
  findUserRecipe,
  onPrevDay,
  onNextDay,
  onJumpToday,
  onTapMeal,
  onTapCook,
  onTapEmpty,
  onRescheduleMeal,   // (meal, newScheduledForDate) → Promise — drag-to-reschedule
  onStartCook,        // (meal) → void — fired from the COOK NOW button in the expanded tile
}) {
  const { theme } = useMCMTheme();
  const today = useMemo(() => startOfDay(new Date()), []);
  const isToday = isSameDay(cursorDate, today);
  const isPastDay = cursorDate.getTime() < today.getTime();

  // Bucket meals + cooks into timeline blocks (collapses cooked
  // planned meals onto their planned slot, keeps standalone cooks).
  const blocks = useMemo(
    () => dayTimelineBlocks(cursorDate, meals, pastCooks, {
      findRecipe, findUserRecipe, pantry,
      prepNotifications: prepRows,
    }),
    [cursorDate, meals, pastCooks, prepRows, findRecipe, findUserRecipe, pantry],
  );
  const totalLanes = useMemo(() => assignLanes(blocks), [blocks]);
  // Horizontal-scroll mode kicks in when ANY block in the day sits
  // in a 3+ lane conflict group. The lane area's content width
  // expands to fit fixed-width tiles side-by-side; the user scrolls
  // right to reach lane 3+. Solo / 2-lane days stay in the standard
  // percentage layout.
  const maxLanesInDay = useMemo(
    () => blocks.reduce((mx, b) => Math.max(mx, b.groupTotalLanes || 1), 1),
    [blocks],
  );
  const needsHScroll = maxLanesInDay >= HSCROLL_THRESHOLD;
  const hscrollContentWidth = needsHScroll
    ? HOUR_LABEL_WIDTH + 12 + maxLanesInDay * (HSCROLL_LANE_WIDTH + HSCROLL_LANE_GAP)
    : null;

  // In-place expand: tapping a tile selects it; the selected tile
  // takes ~80% of the timeline width and the others collapse to slim
  // strips on the right. Tap selected = deselect. Tap empty hour =
  // deselect first; if nothing was selected, fall through to the
  // recipe picker.
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => { setSelectedId(null); }, [cursorDate]);

  // When a tile is selected, re-allocate lanes among the *other*
  // tiles so two collapsed siblings don't render at the same x
  // position (the bug where Jalapeño + Broccoli stacked on top of
  // each other in the 18% strip). Each non-selected block gets its
  // own column inside the strip; widths shrink proportionally with
  // overlap count.
  // The selected block — used to scope "collapsed sibling" behavior
  // to its conflict group only. Tiles that don't share a group with
  // the selected tile keep their normal layout.
  const selectedBlock = useMemo(
    () => selectedId ? blocks.find(b => b.id === selectedId) : null,
    [blocks, selectedId],
  );
  const otherLanes = useMemo(() => {
    if (!selectedBlock) return null;
    // Only the OTHER blocks in the SAME conflict group get
    // re-allocated into the strip. Tiles outside the group stay put.
    const others = blocks.filter(b =>
      b.id !== selectedBlock.id && b.groupId === selectedBlock.groupId,
    );
    const sorted = [...others].sort((a, b) => a.startMinutes - b.startMinutes);
    let groupLaneEnds = [];
    let groupEnd = -Infinity;
    let groupMembers = [];
    const laneMap = new Map();
    const totalsByBlock = new Map();
    const close = () => {
      const total = Math.max(1, groupLaneEnds.length);
      for (const id of groupMembers) totalsByBlock.set(id, total);
      groupLaneEnds = [];
      groupMembers = [];
      groupEnd = -Infinity;
    };
    for (const b of sorted) {
      if (b.startMinutes >= groupEnd) close();
      const blockEnd = b.startMinutes + b.durationMinutes;
      let assigned = -1;
      for (let i = 0; i < groupLaneEnds.length; i++) {
        if (groupLaneEnds[i] <= b.startMinutes) { assigned = i; break; }
      }
      if (assigned === -1) {
        assigned = groupLaneEnds.length;
        groupLaneEnds.push(blockEnd);
      } else {
        groupLaneEnds[assigned] = blockEnd;
      }
      laneMap.set(b.id, assigned);
      groupMembers.push(b.id);
      if (blockEnd > groupEnd) groupEnd = blockEnd;
    }
    close();
    return { laneMap, totalsByBlock };
  }, [blocks, selectedBlock]);

  // Live "now" tick — repaints every 60s on today's view so the
  // greyout + now-line stay accurate.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isToday]);
  const nowMinutes = useMemo(() => {
    const n = new Date(nowTick);
    return n.getHours() * 60 + n.getMinutes();
  }, [nowTick]);
  const nowY = isToday ? minuteToY(nowMinutes) : null;

  // Track horizontal scroll so the hour rail can stay pinned to
  // the left edge of the scrollable viewport when the day's lane
  // area extends past the viewport (3+ concurrent cooks). Without
  // this, the "5 PM / 6 PM / 7 PM" labels scroll out of view as
  // soon as the user swipes right to find lane 3+.
  const [scrollLeft, setScrollLeft] = useState(0);

  // Auto-scroll: today snaps to the now-line on first paint;
  // past/future days snap to the first block (or 8 AM).
  const gridRef = useRef(null);
  useEffect(() => {
    if (!gridRef.current) return;
    let target;
    if (isToday) {
      target = Math.max(0, (nowY ?? 0) - HOUR_ROW_HEIGHT * 1.5);
    } else if (blocks.length > 0) {
      target = Math.max(0, minuteToY(blocks[0].startMinutes) - 8);
    } else {
      target = (8 - DAY_GRID_HOUR_START) * HOUR_ROW_HEIGHT;
    }
    gridRef.current.scrollTop = target;
  }, [cursorDate]); // re-scroll on day navigation

  // Wire scroll listener for the hour rail's translate-X.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const onScroll = () => setScrollLeft(el.scrollLeft);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  // Reset the visual offset whenever the day changes — gridRef's
  // scrollLeft resets to 0 on cursor change but the listener might
  // not fire synchronously.
  useEffect(() => { setScrollLeft(0); }, [cursorDate]);

  const dayHeader = `${DAY_FULL[cursorDate.getDay()]} · ${MONTH_SHORT[cursorDate.getMonth()]} ${cursorDate.getDate()}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)", maxHeight: 720 }}>

      {/* Header — prev / day-label / next */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 4px 12px", gap: 8,
      }}>
        <button onClick={onPrevDay} aria-label="Previous day" style={navBtnStyle(theme)}>‹</button>
        <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 22, color: isToday ? COLOR.gold : (theme.color.skyInk || COLOR.ink),
            lineHeight: 1.1,
          }}>
            {dayHeader}
          </div>
          {isToday && (
            <div style={{
              fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
              color: COLOR.gold, letterSpacing: "0.16em", marginTop: 2,
            }}>
              TODAY
            </div>
          )}
          {!isToday && isPastDay && (
            <div style={{
              fontFamily: FONT.forma, fontSize: 10,
              color: theme.color.skyInkMuted || COLOR.muted, letterSpacing: "0.16em", marginTop: 2,
            }}>
              {Math.round((today.getTime() - cursorDate.getTime()) / 86400000)} DAYS AGO
            </div>
          )}
          {!isToday && !isPastDay && (
            <div style={{
              fontFamily: FONT.forma, fontSize: 10,
              color: theme.color.skyInkMuted || COLOR.muted, letterSpacing: "0.16em", marginTop: 2,
            }}>
              IN {Math.round((cursorDate.getTime() - today.getTime()) / 86400000)} DAYS
            </div>
          )}
        </div>
        <button onClick={onNextDay} aria-label="Next day" style={navBtnStyle(theme)}>›</button>
      </div>

      {!isToday && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
          <button onClick={onJumpToday} style={{
            padding: "6px 16px",
            background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
            color: COLOR.gold, borderRadius: 999,
            fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
            letterSpacing: "0.1em", cursor: "pointer",
          }}>
            JUMP TO TODAY
          </button>
        </div>
      )}

      {/* Timeline — scrollable container. Horizontal scroll engages
          when the day has any 3+ lane conflict group; lane width is
          fixed at HSCROLL_LANE_WIDTH so tiles stay readable instead
          of squishing. */}
      <div ref={gridRef} style={{
        flex: 1,
        overflowY: "auto",
        overflowX: needsHScroll ? "auto" : "hidden",
        background: theme.color.glassFill,
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        border: `1px solid ${theme.color.glassBorder}`,
        borderRadius: 16,
        boxShadow: theme.shadow.glass,
        // Kill text-selection across the entire calendar surface — long
        // press needs to read as "manipulate the tile," not "select the
        // title." Inheritable, but iOS Safari sometimes ignores
        // inheritance, so we also pin it on each tile.
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        // Vertical-swipe always scrolls the timeline, even when the
        // finger lands directly on a tile. Without this, tile pointer
        // handling competes with scroll and the user can't easily get
        // to early-morning / late-night hours.
        touchAction: "pan-y",
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={cursorDate.toDateString()}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
            style={{
              position: "relative",
              minHeight: GRID_TOTAL_HEIGHT,
              minWidth: hscrollContentWidth ?? undefined,
            }}
          >
            {/* Hour grid backdrop — tap target for empty cells. When
                a tile is selected, the empty-cell tap deselects
                instead of opening the recipe picker. */}
            {DAY_GRID_HOURS.map(h => (
              <HourBackdrop
                key={h}
                hour={h}
                theme={theme}
                isPastDay={isPastDay}
                isToday={isToday}
                isPastHour={isToday && h < new Date(nowTick).getHours()}
                onTap={() => {
                  if (selectedId) { setSelectedId(null); return; }
                  onTapEmpty?.(cursorDate, h);
                }}
              />
            ))}

            {/* Pinned hour rail — labels stay at the visible left
                edge regardless of horizontal scroll position. */}
            <HourRail scrollLeft={scrollLeft} theme={theme} />

            {/* Past-day greyout — full overlay over the grid */}
            {isPastDay && (
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0,
                height: GRID_TOTAL_HEIGHT,
                background: "rgba(36,24,16,0.10)",
                pointerEvents: "none",
                zIndex: 1,
              }} />
            )}

            {/* Today greyout — only the past portion (above now-line) */}
            {isToday && nowY != null && nowY > 0 && (
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0,
                height: nowY,
                background: "rgba(36,24,16,0.08)",
                pointerEvents: "none",
                zIndex: 1,
              }} />
            )}

            {/* Now-line — today only, hairline at the current minute */}
            {isToday && nowY != null && (
              <>
                <div style={{
                  position: "absolute", top: nowY - 1, left: 0, right: 0,
                  height: 2, background: COLOR.rose,
                  pointerEvents: "none",
                  zIndex: 3,
                  boxShadow: "0 0 6px rgba(217,107,43,0.45)",
                }} />
                <div style={{
                  position: "absolute", top: nowY - 5, left: 2,
                  width: 8, height: 8, borderRadius: 999,
                  background: COLOR.rose,
                  pointerEvents: "none",
                  zIndex: 3,
                }} />
              </>
            )}

            {/* Timeline blocks — absolutely positioned by start time + duration */}
            {blocks.map(block => (
              <TimelineBlock
                key={block.id}
                block={block}
                cursorDate={cursorDate}
                totalLanes={totalLanes}
                selectedId={selectedId}
                selectedGroupId={selectedBlock?.groupId ?? null}
                otherLanes={otherLanes}
                needsHScroll={needsHScroll}
                userId={userId}
                nameFor={nameFor}
                pantry={pantry}
                onSelect={(id) => setSelectedId(prev => prev === id ? null : id)}
                onOpenDetails={(meal, cookId) => {
                  if (cookId != null) onTapCook?.(cookId);
                  else if (meal)     onTapMeal?.(meal);
                }}
                onRescheduleMeal={onRescheduleMeal}
                onStartCook={onStartCook}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Vertical step bands rendered INSIDE a tile. Each band is sized
// proportionally to its step's share of the recipe's total time, so
// a 3-hour proof reads dominant and a 5-min knead reads small.
// Bands ≥24px tall show step title + duration; thinner bands stay
// as plain colored stripes (still convey shape via height/position).
function StepBands({ steps, availableHeight }) {
  if (!steps || steps.length === 0 || availableHeight < 20) return null;
  const gap = 2;
  const totalGap = gap * Math.max(0, steps.length - 1);
  const usable = Math.max(0, availableHeight - totalGap);
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      gap, marginTop: 4,
      flex: 1, minHeight: 0,
    }}>
      {steps.map((s, i) => {
        const palette = paletteFor(s.index);
        const h = Math.max(6, Math.round(s.weight * usable));
        const showLabel = h >= 24;
        // When the band has more than ~2 line-heights of room, let
        // the title wrap to fill the vertical space instead of
        // truncating with ellipsis. ~16px line-height for the 12px
        // forma title; keep ~10px reserved for top/bottom padding
        // so the wrap doesn't clip on the band edge.
        const lineHeightPx = 16;
        const titleLines = Math.max(1, Math.floor((h - 10) / lineHeightPx));
        const allowWrap = titleLines > 1;
        return (
          <div
            key={i}
            title={`${s.title} · ${formatMinutes(s.minutes)}`}
            style={{
              height: h,
              minHeight: 6,
              display: "flex",
              flexDirection: "column",                                // vertical stack
              alignItems: "flex-start",
              justifyContent: allowWrap ? "flex-start" : "center",
              gap: 2,
              padding: allowWrap ? "8px 10px" : "0 8px",
              background: palette.bg,
              borderLeft: `3px solid ${palette.fg}`,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            {showLabel && (
              <>
                <div style={{
                  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                  color: palette.fg, letterSpacing: "0.01em",
                  width: "100%", minWidth: 0,
                  lineHeight: `${lineHeightPx}px`,
                  overflow: "hidden",
                  ...(allowWrap
                    ? {
                        display: "-webkit-box",
                        WebkitLineClamp: Math.max(1, titleLines - 1),  // reserve a line for the duration below
                        WebkitBoxOrient: "vertical",
                        overflowWrap: "anywhere",
                      }
                    : {
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }),
                }}>
                  {s.title}
                </div>
                <div style={{
                  fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
                  color: palette.fg, opacity: 0.75,
                  lineHeight: `${lineHeightPx}px`,
                }}>
                  {formatMinutes(s.minutes)}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HourBackdrop({ hour, theme, isPastDay, isToday, isPastHour, onTap }) {
  const isPast = isPastDay || isPastHour;
  return (
    <div
      onClick={onTap}
      style={{
        position: "absolute",
        top: (hour - DAY_GRID_HOUR_START) * HOUR_ROW_HEIGHT,
        left: 0, right: 0,
        height: HOUR_ROW_HEIGHT,
        borderTop: `1px solid ${theme.color.glassBorder}`,
        cursor: "pointer",
        transition: "background 160ms ease",
      }}
      // Mouse-only hover tint — same stuck-hover class of bug as the
      // month grid's day border: iOS fires mouseenter on tap without
      // a matching mouseleave, freezing the tint on tapped rows.
      onPointerEnter={e => {
        if (e.pointerType !== "mouse" || isPast) return;
        e.currentTarget.style.background = "rgba(212,166,55,0.06)";
      }}
      onPointerLeave={e => { e.currentTarget.style.background = "transparent"; }}
    />
  );
}

// Pinned hour-label rail. Renders all hour labels in one absolutely-
// positioned column at left:0 of the timeline content, then offsets
// itself horizontally via translateX to track the parent's
// scrollLeft. Result: the column always sits at the visible left
// edge, regardless of where the user has scrolled the lane area.
//
// Lives BEHIND blocks (lower z-index) but ABOVE the hour-line
// backdrops so the labels read clearly. pointerEvents:none so it
// doesn't intercept tile/empty-cell taps.
function HourRail({ scrollLeft, theme }) {
  return (
    <div style={{
      position: "absolute",
      top: 0, left: 0,
      width: HOUR_LABEL_WIDTH,
      height: GRID_TOTAL_HEIGHT,
      transform: `translateX(${scrollLeft}px)`,
      zIndex: 4,
      pointerEvents: "none",
      background: `linear-gradient(to right, ${theme.color.glassFillHeavy} 70%, transparent 100%)`,
      willChange: "transform",
    }}>
      {DAY_GRID_HOURS.map(h => (
        <div
          key={h}
          style={{
            position: "absolute",
            top: (h - DAY_GRID_HOUR_START) * HOUR_ROW_HEIGHT + 4,
            left: 12,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            color: theme.color.skyInkMuted || COLOR.muted,
            letterSpacing: "0.06em",
          }}
        >
          {fmtHourLabel(h)}
        </div>
      ))}
    </div>
  );
}

function TimelineBlock({ block, cursorDate, totalLanes, selectedId, selectedGroupId, otherLanes, needsHScroll, userId, nameFor, pantry, onSelect, onOpenDetails, onRescheduleMeal, onStartCook }) {
  const top    = minuteToY(block.startMinutes);
  const height = durationToHeight(block.durationMinutes);
  const isSelected     = selectedId === block.id;
  // Only siblings in the same conflict group get the "collapsed
  // strip" treatment when something is selected. A Prosciutto at
  // 3 PM in its own group should NOT shrink when the brioche group
  // (5:30 PM) gets a tile selected — they don't share air space.
  const isOtherActive  = selectedId != null && !isSelected
                          && selectedGroupId != null
                          && block.groupId === selectedGroupId;

  // Layout in three modes:
  //   - default:   global lane-based layout
  //   - selected:  this tile takes 80% of the timeline width
  //   - collapsed: another tile is selected; this one lives inside
  //                the 18% strip on the right. Lanes get re-assigned
  //                AMONG non-selected blocks (otherLanes) so two
  //                concurrent collapsed siblings don't pile on top
  //                of each other at the same x-coordinate — they
  //                share the strip side-by-side.
  let leftOffsetCss, widthCss;
  if (isSelected) {
    // Selected tile takes 80% of the row. The other 20% becomes a
    // strip on the right for siblings in the same conflict group.
    // 80/20 instead of 50/50 because in 2-lane groups (default 50%
    // width per tile), a 50% expansion produces zero visible
    // change — the tile's width was already 50%. 80% always reads
    // as "this one got bigger" regardless of original lane count.
    leftOffsetCss = `calc(${HOUR_LABEL_WIDTH + 4}px)`;
    widthCss      = `calc(80% - ${HOUR_LABEL_WIDTH + 8}px)`;
  } else if (isOtherActive) {
    const STRIP_LEFT_PCT  = 81;
    const STRIP_WIDTH_PCT = 19;
    const lane = otherLanes?.laneMap?.get(block.id) ?? 0;
    const total = Math.max(1, otherLanes?.totalsByBlock?.get(block.id) ?? 1);
    const cellWidth = STRIP_WIDTH_PCT / total;
    leftOffsetCss = `calc(${STRIP_LEFT_PCT + lane * cellWidth}% + 2px)`;
    widthCss      = `calc(${cellWidth}% - 4px)`;
  } else if (needsHScroll && (block.groupTotalLanes || 1) >= HSCROLL_THRESHOLD) {
    // Fixed-width column inside a horizontally scrollable lane area.
    // Solo blocks in the same day still use percentage layout
    // (handled in the else branch below) so they fill their row;
    // only multi-lane crowds are forced into fixed-width scroll.
    const leftPx = HOUR_LABEL_WIDTH + 8 + block.lane * (HSCROLL_LANE_WIDTH + HSCROLL_LANE_GAP);
    leftOffsetCss = `${leftPx}px`;
    widthCss      = `${HSCROLL_LANE_WIDTH}px`;
  } else {
    // Width comes from THIS block's conflict group, not the day-wide
    // max. A short cook with no overlap fills its row even when
    // another part of the day has 3 concurrent tiles.
    const lanesInGroup = Math.max(1, block.groupTotalLanes || 1);
    const laneWidthPct = 100 / lanesInGroup;
    const leftPct  = block.lane * laneWidthPct;
    leftOffsetCss = `calc(${leftPct}% + ${block.lane === 0 ? HOUR_LABEL_WIDTH + 4 : 4}px)`;
    widthCss      = `calc(${laneWidthPct}% - ${block.lane === 0 ? HOUR_LABEL_WIDTH + 8 : 8}px)`;
  }

  const handleOpenDetails = () => onOpenDetails?.(block.meal, block.cook?.id);
  // Position-gated tap behavior. The first hour (top 56px) of every
  // tile is the "head" — tapping there opens the drawer. Below that
  // (long-cook body) toggles in-place expand to 80% width.
  //
  // Why this falls out cleanly for both short and long cooks:
  //   - A 30-min tile is ~28px tall; yWithin is always < 56, so the
  //     entire tile reads as head → drawer.
  //   - A 6-hour tile is 336px tall; the top 56px is head → drawer,
  //     and the remaining 280px is body → expand to see step bands.
  // No duration branch needed — the geometry decides.
  // Tiles too short to host a body always open the drawer — the
  // entire tile is head-zone and there's no room for an expand state
  // that fits step bands. Continuation tails (continuesFromPrev) now
  // carry their own per-day step bands, so they participate in the
  // normal head/body split like any other tile.
  const handleTileClick = (yWithin) => {
    const tileIsAllHead = height <= HOUR_ROW_HEIGHT + 8;
    // Expand-mode only makes sense when this tile shares lanes with
    // another. A solo tile shrinking to 80% with empty space on the
    // right is confusing — just open the drawer instead.
    const hasConflicts = (block.groupTotalLanes || 1) > 1;
    if (tileIsAllHead || !hasConflicts) {
      onSelect?.(null);
      handleOpenDetails();
      return;
    }
    if (!Number.isFinite(yWithin) || yWithin < HOUR_ROW_HEIGHT) {
      onSelect?.(null);
      handleOpenDetails();
      return;
    }
    onSelect?.(block.id);
  };

  if (block.kind === "prep") {
    return (
      <PrepTile
        block={block}
        top={top}
        height={height}
        leftCss={leftOffsetCss}
        widthCss={widthCss}
        onClick={handleOpenDetails}
      />
    );
  }
  if (block.kind === "cooked") {
    return (
      <CookedTile
        block={block}
        top={top}
        height={height}
        leftCss={leftOffsetCss}
        widthCss={widthCss}
        isSelected={isSelected}
        isCollapsed={isOtherActive}
        userId={userId}
        nameFor={nameFor}
        onClick={handleTileClick}
        onOpenDetails={handleOpenDetails}
      />
    );
  }
  // Drag-to-reschedule wiring. Only fires on planned/requested/leftover
  // tiles (cooked tiles aren't draggable — that cook already happened).
  // 15-minute snap quantizes the drag-end position so a tiny accidental
  // drag doesn't move the meal off its intended hour by 3 minutes.
  const onDragEnd = (info) => {
    if (!onRescheduleMeal || !block.meal || !info?.offset) return;
    const dy = info.offset.y;
    if (Math.abs(dy) < 6) return;        // smaller than a tap → no-op
    const minutesDelta = Math.round((dy / HOUR_ROW_HEIGHT) * 60 / 15) * 15;
    if (minutesDelta === 0) return;
    const next = new Date(block.meal.scheduled_for);
    next.setMinutes(next.getMinutes() + minutesDelta);
    onRescheduleMeal(block.meal, next);
  };
  return (
    <PlannedTile
      block={block}
      top={top}
      height={height}
      leftCss={leftOffsetCss}
      widthCss={widthCss}
      isSelected={isSelected}
      isCollapsed={isOtherActive}
      userId={userId}
      nameFor={nameFor}
      pantry={pantry}
      onClick={handleTileClick}
      onDragEnd={onDragEnd}
    />
  );
}

// Long-press threshold before drag-to-reschedule arms. 400ms is the
// sweet spot: long enough that a stray scroll or accidental finger
// touch never moves a meal, short enough that an intentional press
// reads as snappy. Apple's standard long-press is 500ms; we shave a
// bit because the tiles are big targets and the user is already in a
// "manipulate the calendar" mental mode.
const DRAG_LONG_PRESS_MS = 400;
// Pointer can wiggle up to 6px during the press without cancelling —
// covers natural finger jitter while resting on the tile. Past this
// radius before the timer fires, the gesture reads as scroll-intent
// and we abort the drag-arm.
const DRAG_ARM_MOVE_TOLERANCE = 6;

function PlannedTile({ block, top, height, leftCss, widthCss, isSelected, isCollapsed, userId, nameFor, pantry, onClick, onDragEnd }) {
  // Drag-vs-tap arbitration: framer-motion's `dragListener={false}` +
  // useDragControls means a tile NEVER initiates drag from a normal
  // pointer-down. We arm drag manually after a long-press, so casual
  // taps + page scrolls + short presses behave as taps; only a
  // deliberate press-and-hold triggers reschedule.
  const tileRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressOriginRef = useRef(null);
  // dragState: null when not dragging; { startY, currentY, pointerId }
  // when actively being dragged. Drives the live transform + final
  // delta computation on pointerup.
  const [dragState, setDragState] = useState(null);
  const dragStateRef = useRef(null);
  dragStateRef.current = dragState;

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressOriginRef.current = null;
  };
  // Cleanup on unmount.
  useEffect(() => () => cancelLongPress(), []);

  // Window-level move/up listeners while dragging — finger drift off
  // the tile keeps working, and the gesture survives even if the
  // pointer leaves the original element.
  useEffect(() => {
    if (!dragState) return undefined;
    const onMove = (e) => {
      if (e.pointerId !== dragState.pointerId) return;
      setDragState((prev) => prev ? { ...prev, currentY: e.clientY } : null);
    };
    const onUp = (e) => {
      if (e.pointerId !== dragState.pointerId) return;
      const dy = e.clientY - dragState.startY;
      setDragState(null);
      if (Math.abs(dy) >= 6 && onDragEnd) {
        // Mirror framer-motion's info shape so the parent's onDragEnd
        // handler — which already expects { offset: { y } } — works
        // unchanged.
        onDragEnd({ offset: { y: dy } });
      }
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragState, onDragEnd]);
  const { meal, recipe } = block;
  if (!meal) return null;
  const isRequest    = block.kind === "requested";
  const isLeftover   = block.kind === "leftover";
  const pantryEatRow = isLeftover && meal.from_pantry_row_id
    ? (pantry || []).find(p => p.id === meal.from_pantry_row_id)
    : null;
  const cookLabel = isLeftover
    ? "Eat from pantry"
    : isRequest
      ? `Requested by ${nameFor ? nameFor(meal.user_id) : "someone"}`
      : nameFor ? nameFor(meal.cook_id) : "";
  // Title source priority — resolved recipe wins (it's the only
  // place the human-authored title lives). scheduled_meals doesn't
  // store recipe_title, so a slug like "ultra-rich-brioche-loaf"
  // would surface verbatim if we read meal.recipe_slug. Pantry
  // leftovers use their own row name; recipe-cook meals use the
  // recipe's title.
  const displayTitle = recipe?.title
    || pantryEatRow?.name
    || prettifySlug(meal.recipe_slug)
    || "Meal";

  const compact = height < 56;
  const padding = compact ? 6 : 10;
  const isMine = meal.cook_id === userId;
  const attributionLabel = isLeftover
    ? "Eat from pantry"
    : isRequest
      ? `Requested by ${nameFor ? nameFor(meal.user_id) : "someone"}`
      : (!isMine && cookLabel ? cookLabel : null);

  // Step bands re-emerged inside the tile. Drawer drill-down is the
  // SECONDARY surface; the inline timeline is the PRIMARY visual on
  // long-cook days. Reserve room for header rows (title + meta + maybe
  // attribution) before bands take the rest.
  const showBands = !compact && Array.isArray(block.steps) && height >= 110;
  const headerRows = (attributionLabel && !compact) ? 3 : 2;
  const headerHeight = compact ? 22 : (headerRows * 18 + 8);
  const availableForBands = height - headerHeight - padding * 2;

  // Visual offset while dragging. Applied via inline transform so the
  // tile follows the finger smoothly without React re-rendering its
  // children — only this component re-renders per pointermove.
  const dragOffsetY = dragState ? dragState.currentY - dragState.startY : 0;
  const isDragging = dragState != null;

  return (
    <motion.button
      ref={tileRef}
      onTap={(e, info) => {
        // onTap fires on a clean down→up. If a drag started, we cancel
        // it via setDragState(null) before this would fire — but
        // belt-and-suspenders: skip onTap if a drag was active.
        if (isDragging) return;
        const rect = tileRef.current?.getBoundingClientRect();
        // CLIENTY, not info.point.y — framer's info.point is PAGE-
        // relative (includes scroll offset) on iOS Safari, while
        // getBoundingClientRect returns VIEWPORT-relative coords. On
        // a scrolled day view (mobile, common), info.point.y - rect.top
        // overshoots by the scroll amount, pushing yWithin past
        // HOUR_ROW_HEIGHT every time, so handleTileClick always took
        // the "expand body" branch instead of "open meal drawer."
        // The native PointerEvent's clientY is consistently viewport-
        // relative across browsers — math agrees with rect.top.
        const clientY = e?.clientY ?? e?.nativeEvent?.clientY ?? info?.point?.y;
        const yWithin = (rect && clientY != null) ? clientY - rect.top : 0;
        onClick?.(yWithin);
      }}
      onPointerDown={(e) => {
        if (dragStateRef.current) return; // already dragging
        const pointerId = e.pointerId;
        const startY = e.clientY;
        const startX = e.clientX;
        longPressOriginRef.current = { x: startX, y: startY };
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          if (typeof navigator !== "undefined" && navigator.vibrate) {
            try { navigator.vibrate(10); } catch (_) { /* ignore */ }
          }
          // Arm the drag — window-level listeners (in the useEffect
          // above) take over from here.
          setDragState({ pointerId, startY, currentY: startY });
        }, DRAG_LONG_PRESS_MS);
      }}
      onPointerMove={(e) => {
        if (!longPressOriginRef.current || !longPressTimerRef.current) return;
        const dx = e.clientX - longPressOriginRef.current.x;
        const dy = e.clientY - longPressOriginRef.current.y;
        if (Math.hypot(dx, dy) > DRAG_ARM_MOVE_TOLERANCE) cancelLongPress();
      }}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onPointerLeave={cancelLongPress}
      style={{
        position: "absolute",
        top, height,
        left: leftCss, width: widthCss,
        padding: `${padding}px ${padding + 2}px`,
        background: isRequest ? "rgba(212,166,55,0.22)" : "rgba(255,255,255,0.42)",
        backdropFilter: "blur(16px) saturate(160%)",
        WebkitBackdropFilter: "blur(16px) saturate(160%)",
        border: `1px solid ${isRequest ? "rgba(212,166,55,0.55)" : "rgba(255,255,255,0.78)"}`,
        borderLeft: `2px solid ${COLOR.gold}`,
        // Continuation tiles square off the boundary edge so they
        // visually "connect" to the adjacent day. A cook that runs
        // Sat 11:30 PM → Sun 9 AM reads as one continuous shape
        // across the day boundary.
        borderRadius: block.continuesFromPrev && block.continuesIntoNext ? 0
          : block.continuesFromPrev ? "0 0 8px 8px"
          : block.continuesIntoNext ? "8px 8px 0 0"
          : 8,
        cursor: isDragging ? "grabbing" : "grab",
        boxShadow: isDragging
          ? "0 12px 32px rgba(36,24,16,0.22)"
          : "0 1px 0 rgba(36,24,16,0.05)",
        textAlign: "left",
        zIndex: isDragging ? 5 : 2,
        overflow: "hidden",
        display: "flex", flexDirection: "column", gap: 4,
        // While dragging: translate by the live offset and pop scale
        // for visual feedback. transform composites on the GPU so this
        // is cheap even at 60fps pointermove rate.
        transform: isDragging ? `translateY(${dragOffsetY}px) scale(1.04)` : undefined,
        // touch-action recipe:
        //   - while dragging: "none" — full pointer control, no scroll.
        //   - otherwise: "pan-y" — vertical scroll passes through the
        //     tile so the user can scroll past it without having to
        //     find empty space first. Long-press still arms via my
        //     pointer handlers regardless.
        touchAction: isDragging ? "none" : "pan-y",
        // Long-press on text otherwise triggers selection / iOS
        // magnifier — we want the press to read as "manipulate the
        // tile," not "copy the title." Inheritance through the gridRef
        // wrapper covers most cases; redundant here for stubborn iOS
        // Safari builds.
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div style={titleStyle(compact, height)}>
        {displayTitle}
      </div>
      {/* Continuation badge — utility glyph + day name, no full prose.
          Sits ABOVE the time line because it's a higher-level fact:
          "this is a sliver of a longer cook." */}
      {!compact && (block.continuesFromPrev || block.continuesIntoNext) && (
        <div style={continuationLineStyle(isRequest)}>
          {block.continuesFromPrev && (
            <>‹ {block.isOvernightDay2 ? "Day 1" : "from"} {prevDayLabel(block.time)}{block.isOvernightDay2 ? " evening" : ""}</>
          )}
          {block.continuesFromPrev && block.continuesIntoNext ? " · " : ""}
          {block.continuesIntoNext && (
            <>{block.isOvernightDay1 ? "Day 2" : "to"} {nextDayLabel(block.time)}{block.isOvernightDay1 ? " morning" : ""} ›</>
          )}
        </div>
      )}
      {!compact && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
          color: isRequest ? COLOR.gold : COLOR.muted,
          letterSpacing: "0.02em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {fmtTime(block.time)} · {formatMinutes(block.fullDurationMinutes || block.durationMinutes)}
          {meal.servings != null && !isLeftover ? ` · ${meal.servings} servings` : ""}
        </div>
      )}
      {/* Eat-anchored tiles surface the eat-time on the tile itself
          so the user knows at a glance "this brioche is for Sun
          breakfast even though it cooks Saturday night." */}
      {!compact && meal.eat_anchor && height >= 92 && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
          color: COLOR.gold,
          letterSpacing: "0.08em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          EAT {fmtTime(meal.scheduled_for).toUpperCase()}
        </div>
      )}
      {!compact && attributionLabel && height >= 92 && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
          color: isRequest ? COLOR.gold : COLOR.muted,
          letterSpacing: "0.02em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {attributionLabel}
        </div>
      )}
      {showBands && (
        <StepBands steps={block.steps} availableHeight={availableForBands} />
      )}
    </motion.button>
  );
}

const SHORT_DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const prevDayLabel = (d) => {
  const x = new Date(d); x.setDate(x.getDate() - 1);
  return SHORT_DAY_LABELS[x.getDay()];
};
const nextDayLabel = (d) => {
  const x = new Date(d); x.setDate(x.getDate() + 1);
  return SHORT_DAY_LABELS[x.getDay()];
};
const continuationLineStyle = (isRequest) => ({
  fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
  color: isRequest ? COLOR.gold : COLOR.goldDim,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
});

// Title style: 2-line wrap ONLY when the tile is tall enough to
// actually fit two lines + meta + padding. On short tiles the
// second line bleeds past the bottom and clips mid-letter — use
// single-line ellipsis instead. Threshold: ~90px (enough for
// 2 lines @ 19.2px + meta line + padding).
function titleStyle(compact, tileHeight) {
  const allowTwoLines = !compact && tileHeight >= 92;
  const base = {
    fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
    fontSize: compact ? 14 : 16, color: COLOR.ink,
    lineHeight: 1.2, minWidth: 0,
    overflow: "hidden",
    overflowWrap: "anywhere",   // allow break inside long words
  };
  if (allowTwoLines) {
    return {
      ...base,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    };
  }
  return {
    ...base,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  };
}

// PrepTile — thin reminder tile for a queued prep_notifications row.
// Visually distinct from cook tiles: cool teal palette (cook tiles
// run on warm gold/cream). Smaller, height-clamped, no step bands,
// no drag-to-reschedule. Tap opens the source meal's drawer.
function PrepTile({ block, top, height, leftCss, widthCss, onClick }) {
  const { prep, meal } = block;
  const fired = !!prep?.delivered;
  const sourceTitle = meal?.recipe_slug ? prettifySlug(meal.recipe_slug) : null;
  const visualHeight = Math.min(height, 56);
  // Cool teal palette so prep tiles read at-a-glance as "different
  // from the cook" — cooks are gold/cream, preps are teal. fired
  // (delivered) state dims the saturation but keeps the hue.
  const TEAL_BG     = fired ? "rgba(70,140,140,0.10)" : "rgba(70,140,140,0.20)";
  const TEAL_BORDER = fired ? "rgba(70,140,140,0.42)" : "rgba(70,140,140,0.65)";
  const TEAL_INK    = "#2F6F6F";
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        position: "absolute",
        top, height: visualHeight,
        left: leftCss, width: widthCss,
        padding: "6px 10px",
        background: TEAL_BG,
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        border: `1px dashed ${TEAL_BORDER}`,
        borderLeft: `3px solid ${TEAL_INK}`,
        borderRadius: 6,
        cursor: "pointer",
        textAlign: "left",
        zIndex: 1,
        overflow: "hidden",
        opacity: fired ? 0.62 : 1,
        display: "flex", alignItems: "center", gap: 8,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: "pan-y",
      }}
    >
      <span style={{ fontSize: 14, flexShrink: 0 }}>{prep?.emoji || "⏰"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
          color: TEAL_INK,
          letterSpacing: "0.01em",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {prep?.title || "Prep reminder"}
        </div>
        <div style={{
          fontFamily: FONT.mono, fontSize: 9,
          color: COLOR.muted, letterSpacing: "0.06em",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fmtTime(block.time)}{sourceTitle ? ` · for ${sourceTitle}` : ""}{fired ? " · SENT" : ""}
        </div>
      </div>
    </button>
  );
}

function CookedTile({ block, top, height, leftCss, widthCss, userId, nameFor, onClick }) {
  const tileRef = useRef(null);
  const { cook, meal, recipe } = block;
  const chefId = cook?.user_id ?? meal?.cook_id ?? meal?.user_id ?? null;
  const chef = chefId && nameFor ? nameFor(chefId) : "";
  const isMine = chefId === userId;
  const title = recipe?.title
    || cook?.recipe_title
    || prettifySlug(meal?.recipe_slug || cook?.recipe_slug)
    || "Cooked";
  const compact = height < 56;
  const padding = compact ? 6 : 10;
  const showBands = !compact && Array.isArray(block.steps) && height >= 110;
  const headerHeight = compact ? 22 : 44;
  const availableForBands = height - headerHeight - padding * 2;

  return (
    <button
      ref={tileRef}
      onClick={(e) => {
        e.stopPropagation();
        const rect = tileRef.current?.getBoundingClientRect();
        const yWithin = rect ? e.clientY - rect.top : 0;
        onClick?.(yWithin);
      }}
      style={{
        position: "absolute",
        top, height,
        left: leftCss, width: widthCss,
        padding: `${padding}px ${padding + 2}px`,
        background: "rgba(180,200,160,0.30)",
        backdropFilter: "blur(16px) saturate(150%)",
        WebkitBackdropFilter: "blur(16px) saturate(150%)",
        border: "1px solid rgba(180,200,160,0.55)",
        borderLeft: `2px solid ${COLOR.leaf}`,
        borderRadius: block.continuesFromPrev && block.continuesIntoNext ? 0
          : block.continuesFromPrev ? "0 0 8px 8px"
          : block.continuesIntoNext ? "8px 8px 0 0"
          : 8,
        cursor: "pointer",
        boxShadow: "0 1px 0 rgba(36,24,16,0.04)",
        textAlign: "left",
        zIndex: 2,
        overflow: "hidden",
        display: "flex", flexDirection: "column", gap: 4,
        opacity: 0.88,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: "pan-y",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <span style={{ ...titleStyle(compact, height), flex: 1 }}>
          {title}
        </span>
        <span
          aria-label="Cooked"
          title="Cooked"
          style={{
            fontFamily: FONT.forma, fontSize: 14, fontWeight: 700,
            color: COLOR.leaf, flexShrink: 0,
            marginTop: 2, lineHeight: 1,
          }}
        >
          ✓
        </span>
      </div>
      {!compact && (block.continuesFromPrev || block.continuesIntoNext) && (
        <div style={{ ...continuationLineStyle(false), color: COLOR.leafMuted }}>
          {block.continuesFromPrev && (
            <>‹ {block.isOvernightDay2 ? "Day 1" : "from"} {prevDayLabel(block.time)}{block.isOvernightDay2 ? " evening" : ""}</>
          )}
          {block.continuesFromPrev && block.continuesIntoNext ? " · " : ""}
          {block.continuesIntoNext && (
            <>{block.isOvernightDay1 ? "Day 2" : "to"} {nextDayLabel(block.time)}{block.isOvernightDay1 ? " morning" : ""} ›</>
          )}
        </div>
      )}
      {!compact && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
          color: COLOR.leafMuted, letterSpacing: "0.02em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {fmtTime(block.time)} · {formatMinutes(block.durationMinutes)}
          {!isMine && chef ? ` · ${chef}` : ""}
          {Array.isArray(cook?.diners) && cook.diners.length > 0
            ? ` · ${cook.diners.length} ${cook.diners.length === 1 ? "diner" : "diners"}`
            : ""}
        </div>
      )}
      {showBands && (
        <StepBands steps={block.steps} availableHeight={availableForBands} />
      )}
    </button>
  );
}

function navBtnStyle(theme) {
  return {
    width: 36, height: 36, flexShrink: 0,
    borderRadius: 999,
    border: `1px solid ${theme.color.glassBorder}`,
    background: theme.color.glassFillHeavy,
    color: theme.color.skyInk || COLOR.ink,
    fontFamily: FONT.forma, fontSize: 20, fontWeight: 500,
    cursor: "pointer", padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    boxShadow: theme.shadow.soft,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  };
}
